/**
 * Web substitute for `react-native-snap-carousel` (RP-10858, StoreV2 web port).
 *
 * The real package (v3) is class-based and has no react-native-web story, so it's
 * not installed for portal-play. StoreV2's LTOCarousel only uses a small slice of
 * its API — the imperative ref methods `snapToNext / snapToPrev / snapToItem` plus
 * `data / renderItem / sliderWidth / itemWidth / onSnapToItem` — so this is a thin
 * horizontal, snap-scrolling `ScrollView` that exposes exactly that surface. Same
 * pattern as the other src/substitute-dependencies shims.
 *
 * ponytail: naive centering (sidePad = (slider-item)/2) + round(offset/itemWidth)
 * index detection — good enough for the LTO banner strip; tune the snap math here if
 * a future consumer needs pixel-exact paging.
 */
import React, {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import {ScrollView, View, Dimensions, type NativeSyntheticEvent, type NativeScrollEvent} from 'react-native';

export interface CarouselProps {
  data: any[];
  // `any` (not ReactNode/ReactElement): the app pulls two @types/react versions whose
  // ReactNode types are mutually unassignable, so a concrete return type trips the
  // View children overload. This is a shim boundary — `any` is the pragmatic choice.
  renderItem: (info: {item: any; index: number}) => any;
  sliderWidth?: number;
  itemWidth?: number;
  firstItem?: number;
  onSnapToItem?: (index: number) => void;
  containerCustomStyle?: any;
  slideStyle?: any;
  // Accepted but ignored (visual-only in the native lib): layout, inactiveSlideScale,
  // inactiveSlideOpacity, activeSlideAlignment, enableMomentum, enableSnap, autoplay,
  // removeClippedSubviews, useScrollView, etc.
  [key: string]: any;
}

export interface CarouselHandle {
  snapToNext: () => void;
  snapToPrev: () => void;
  snapToItem: (index: number, animated?: boolean) => void;
}

function CarouselInner(props: CarouselProps, ref: React.Ref<CarouselHandle>) {
  const {
    data = [],
    renderItem,
    sliderWidth,
    itemWidth,
    firstItem = 0,
    onSnapToItem,
    containerCustomStyle,
    slideStyle,
  } = props;

  const scrollRef = useRef<ScrollView>(null);
  const indexRef = useRef<number>(firstItem);
  // Pending settle timers (see scrollToIndex). Tracked so a new snap clears the stale ones
  // and unmount clears them all — otherwise each snap leaks four timeouts holding the scroller.
  const settleTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(
    () => () => {
      settleTimers.current.forEach(clearTimeout);
      settleTimers.current = [];
    },
    []
  );

  const sw = sliderWidth || Dimensions.get('window').width;
  const iw = itemWidth || sw;
  // Center the active slide by padding each side by half the empty gutter — UNLESS
  // alignStart (a full-width left-aligned rail: first card at the left, the rest bleed
  // right), which the desktop LTO rail uses to line up with the games grid.
  const startPad = typeof props.startPad === 'number' ? props.startPad : 0;
  const sidePad = props.alignStart ? startPad : Math.max(0, (sw - iw) / 2);
  // Uniform card shrink (see the item wrapper). 1 = no scale.
  const itemScale = typeof props.webItemScale === 'number' ? props.webItemScale : 1;

  const scrollToIndex = (i: number, animated = true) => {
    if (data.length === 0) return;
    const clamped = Math.max(0, Math.min(i, data.length - 1));
    indexRef.current = clamped;
    const left = clamped * iw;

    // react-native-web renders ScrollView as an OUTER element wrapping the scrollable
    // one, and the forwarded ref is the outer node — calling scrollTo() on it is a silent
    // no-op, which is why the rewardz tier deck could not be arrowed or dotted past the
    // card it opened on (the consumer's onSnapToItem still fired, so the dots and the
    // background moved while the cards stayed put). getScrollableNode() is RNW's accessor
    // for the element that actually scrolls; fall back to the ref itself for native/other
    // implementations.
    const scroller: any =
      (scrollRef.current as any)?.getScrollableNode?.() ?? (scrollRef.current as any);

    if (scroller?.scrollTo) {
      // RNW attaches its own scrollTo({x, y, animated}) to that node — NOT the DOM
      // scrollTo({left, top, behavior}) signature. Passing DOM args here silently does
      // nothing, so keep the RN shape.
      scroller.scrollTo({x: left, y: 0, animated});
    }
    // Self-correcting fallback. Two things can swallow the call above: a plain DOM node
    // with no RNW method attached, and — the one that actually bit the rewardz deck — a
    // long animated scroll cancelled part-way by the re-render the tier change kicks off
    // (background swap + lottie remount), which left the deck parked on the old card while
    // the dots and theme had already moved on. So: jump immediately when unanimated, and
    // for an animated scroll check once after it should have landed and hard-correct only
    // if it didn't. A scroll that completed normally is never touched.
    const settle = () => {
      if (!scroller || typeof scroller.scrollLeft !== 'number') return;
      if (Math.abs(scroller.scrollLeft - left) > 2) scroller.scrollLeft = left;
    };
    // Cancel any settle timers still pending from a previous snap — their target is stale now.
    settleTimers.current.forEach(clearTimeout);
    settleTimers.current = [];
    if (!animated) {
      settle();
    } else if (typeof setTimeout === 'function') {
      // Re-assert on a short schedule rather than once: the tier change re-renders the
      // deck (background + lottie remount) and a re-render landing mid-animation can
      // reset the scroller, so a single late check can itself be undone. Each pass is a
      // no-op once the deck is where it should be.
      settleTimers.current = [120, 350, 700, 1100].map((ms) => setTimeout(settle, ms));
    }

    onSnapToItem?.(clamped);
  };

  useImperativeHandle(ref, () => ({
    snapToNext: () =>
      scrollToIndex(indexRef.current + 1 >= data.length ? 0 : indexRef.current + 1),
    snapToPrev: () =>
      scrollToIndex(indexRef.current - 1 < 0 ? data.length - 1 : indexRef.current - 1),
    snapToItem: (i: number, animated = true) => scrollToIndex(i, animated),
  }));

  const handleScrollEnd = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (iw <= 0) return; // no item width yet — division would be NaN/Infinity
    const x = e.nativeEvent.contentOffset.x;
    const i = Math.max(0, Math.min(Math.round(x / iw), data.length - 1));
    if (i !== indexRef.current) {
      indexRef.current = i;
      onSnapToItem?.(i);
    }
  };

  // react-native-web maps onScroll to the DOM scroll event but does NOT emit
  // onScrollEndDrag / onMomentumScrollEnd for a wheel / trackpad / CSS-snap scroll — so
  // a user-driven scroll never updated the consumer's active index (the paging dots only
  // moved on autoplay). Track the index live off scroll position too; the index guard
  // keeps it to one onSnapToItem per card crossed.
  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (iw <= 0) return; // no item width yet — division would be NaN/Infinity
    const x = e.nativeEvent.contentOffset.x;
    const i = Math.max(0, Math.min(Math.round(x / iw), data.length - 1));
    if (i !== indexRef.current) {
      indexRef.current = i;
      onSnapToItem?.(i);
    }
  };

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      snapToInterval={iw}
      decelerationRate='fast'
      contentContainerStyle={{paddingHorizontal: sidePad}}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      onMomentumScrollEnd={handleScrollEnd}
      onScrollEndDrag={handleScrollEnd}
      style={containerCustomStyle}
    >
      {data.map((item, index) => (
        // flexShrink:0 so items keep their itemWidth and the row overflows (→ scrollable)
        // instead of the flex children compressing to fit the viewport (no overflow, no
        // scroll). Belt-and-suspenders for consumers whose wrapper doesn't stretch us.
        <View key={index} style={[{width: iw, flexShrink: 0}, slideStyle]}>
          {itemScale !== 1 ? (
            // Shrink the card as a UNIT. This is a web-only substitute, so a real DOM wrapper
            // is available — and it's the reliable way to get `transform-origin: left top`,
            // which react-native-web (0.19) does NOT forward from a RN style. Anchoring
            // top-left keeps the scaled card on the rail's left gutter; the wrapper's layout
            // box stays the native card width and overflows the (narrower, scaled) slot, but
            // the parent scrolls with overflow visible so nothing clips.
            <div style={{transform: `scale(${itemScale})`, transformOrigin: 'left top'}}>
              {renderItem({item, index})}
            </div>
          ) : (
            renderItem({item, index})
          )}
        </View>
      ))}
    </ScrollView>
  );
}

const Carousel = forwardRef(CarouselInner);

// Some consumers import { Pagination } — StoreV2 doesn't, but keep a no-op so the
// specifier never breaks a build.
export const Pagination = () => null;

// react-native-snap-carousel exposes this interpolation helper; the rewardz deck
// imports it by name. The web substitute never invokes the interpolators (it uses
// CSS scroll-snap), so an inert stub is enough to silence the webpack
// "export not found" warning without pulling in the real animation math.
export const getInputRangeFromIndexes = () => ({inputRange: [] as number[], outputRange: [] as number[]});

export default Carousel;
